import {useContext, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Button, DataTable, IconButton, Title} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {CreditType, useCreditTypes, useFunds} from './../hooks/useDomainData';
import {CreditTypeEditor} from '../components/FormikStoreEditors/CreditTypeEditor';
import {useAppSettings} from '../components/AppSettings';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';

function CreditTypes() {
  const user = useContext(UserContext);
  const person = useAuthPersonInfo();
  const creditTypes = useCreditTypes();
  const funds = useFunds();
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    CreditType | undefined
  >();

  const collectionName = 'creditTypes';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: CreditType) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: CreditType) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Credit Types</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Credit Type
          </Button>
        )}
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {!creditTypes && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {creditTypes && (
            <>
              <DataTable.Header>
                <DataTable.Title>Fund</DataTable.Title>
                <DataTable.Title style={styles.widerCell}>
                  Credit Type
                </DataTable.Title>
                <DataTable.Title numberOfLines={2} style={[styles.borderCell]}>
                  Allow Reservation
                </DataTable.Title>
                <DataTable.Title numberOfLines={2} style={styles.borderCell}>
                  Allow Fee Offset
                </DataTable.Title>
                <DataTable.Title numberOfLines={2} style={styles.borderCell}>
                  Capital Dividend
                </DataTable.Title>
                <DataTable.Title numberOfLines={2} style={styles.borderCell}>
                  External Exchange
                </DataTable.Title>
                <DataTable.Title numberOfLines={2} style={styles.borderCell}>
                  Fund Exchange
                </DataTable.Title>
                {(person?.isAdmin || person?.isGuestRelations) && (
                  <DataTable.Title numberOfLines={2} style={styles.borderCell}>
                    Actions
                  </DataTable.Title>
                )}
              </DataTable.Header>
            </>
          )}
          {creditTypes && creditTypes.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No credit types found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {creditTypes &&
            creditTypes.length > 0 &&
            creditTypes.map(firestoreObject => (
              <DataTable.Row key={firestoreObject.creditTypeId}>
                <DataTable.Cell>
                  {firestoreObject.fund?.fundCode}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {firestoreObject.creditType}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {firestoreObject.allowReservation && <Icon name={'check'} />}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {firestoreObject.allowAFO && <Icon name={'check'} />}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {firestoreObject.allowCD && <Icon name={'check'} />}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {firestoreObject.allowECC && <Icon name={'check'} />}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {firestoreObject.allowFCC && <Icon name={'check'} />}
                </DataTable.Cell>
                {(person?.isAdmin || person?.isGuestRelations) && (
                  <DataTable.Cell style={styles.borderCell}>
                    <>
                      {(person?.isAdmin || person?.isGuestRelations) && (
                        <IconButton
                          icon="pencil"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() =>
                            handleEditFirestoreObject(firestoreObject)
                          }
                        />
                      )}
                      {person?.isAdmin && (
                        <IconButton
                          icon="delete"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() =>
                            handleDeleteFirestoreObject.mutate(firestoreObject)
                          }
                        />
                      )}
                    </>
                  </DataTable.Cell>
                )}
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>

      <CreditTypeEditor
        funds={funds ?? []}
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        creditType={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  widerCell: {
    minWidth: 200,
    width: 200,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  noRowSeparation: {
    borderBottomWidth: 0,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default CreditTypes;
